import { Form } from 'antd';
import React, { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { globalConfirmModalActions } from 'app/globalConfirmModal';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useChangeSizeCodesPricesMutation } from 'entities/SizeCode';
import { UserPermissions } from 'entities/User';
import { Button } from 'shared/ui/Button';
import { CheckboxGroup } from 'shared/ui/CheckboxGroup';
import { InputNumber } from 'shared/ui/InputNumber';
import { RadioButtonGroup } from 'shared/ui/RadioButtonGroup';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { getPriceChangeParams, getSelectedSizeCodes, getSelectedWarehouse } from '../../model/selectors';
import { changePricesModalActions } from '../../model/slice/changePricesModalSlice';
import { PriceChangeDirection, PriceChangeParams, RateType } from '../../model/types';
import { PricesPreview } from './PricesPreview';

interface ChangePricesStepProps {
  onClose: () => void;
}

export const ChangePricesStep: FC<ChangePricesStepProps> = memo((props) => {
  const { onClose } = props;

  const { t } = useAppTranslation(['boxes', 'common']);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<PriceChangeParams>();

  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const selectedSizeCodes = useAppSelector(getSelectedSizeCodes);
  const priceChangeParams = useAppSelector(getPriceChangeParams);

  const isForbidden = useCheckPermission(UserPermissions.EDIT_UNITS);
  const [changeSizeCodesPrices, { isLoading }] = useChangeSizeCodesPricesMutation();

  const closeConfirmModal = useCallback((): void => {
    dispatch(globalConfirmModalActions.setGlobalConfirmModalData(null));
  }, [dispatch]);

  const setConfirmModalLoading = useCallback(
    (isLoading: boolean): void => {
      dispatch(globalConfirmModalActions.setGlobalConfirmModalLoading(isLoading));
    },
    [dispatch],
  );

  useEffect(() => {
    form.setFieldsValue(priceChangeParams);
    // persist happens in onValuesChange; do not sync redux back into the form on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const directionOptions = useMemo(
    () => [
      { label: t('Increase'), value: PriceChangeDirection.INCREASE },
      { label: t('Decrease'), value: PriceChangeDirection.DECREASE },
    ],
    [t],
  );

  const rateTypeOptions = useMemo(
    () => [
      { label: t('Daily rate'), value: RateType.DAILY_RATE },
      { label: t('Week rate'), value: RateType.WEEK_RATE },
      { label: t('Month rate'), value: RateType.MONTH_RATE },
    ],
    [t],
  );

  const changeDirection = useCallback(
    (direction: PriceChangeDirection): void => {
      form.setFieldValue('direction', direction);
      dispatch(
        changePricesModalActions.setPriceChangeParams({
          ...priceChangeParams,
          direction,
        }),
      );
    },
    [dispatch, form, priceChangeParams],
  );

  const handleValuesChange = useCallback(
    (_: Partial<PriceChangeParams>, values: PriceChangeParams): void => {
      dispatch(
        changePricesModalActions.setPriceChangeParams({
          direction: values.direction || PriceChangeDirection.INCREASE,
          percent: values.percent ?? null,
          rateTypes: values.rateTypes || [],
        }),
      );
    },
    [dispatch],
  );

  const submitChangePrices = useCallback(
    async (updateBoxesPrice = false): Promise<void> => {
      const { direction, percent, rateTypes } = form.getFieldsValue();

      if (!percent || !rateTypes?.length) {
        return;
      }

      try {
        const sizeCodesToUpdate = selectedSizeCodes.filter((sizeCode) =>
          rateTypes.some((rateType) => sizeCode[rateType] !== null && sizeCode[rateType] !== undefined),
        );

        if (!sizeCodesToUpdate.length) {
          showNotification('warning', t('Warning', { ns: 'common' }), t('Selected sizes do not have the chosen rates'));
          closeConfirmModal();
          return;
        }

        setConfirmModalLoading(true);

        await changeSizeCodesPrices({
          direction,
          sizeCodeIds: sizeCodesToUpdate.map((sizeCode) => sizeCode.sizeCodeId),
          percent,
          shouldUpdateBoxes: updateBoxesPrice,
          rateTypes,
        }).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Prices have been successfully updated'));
        onClose();
        closeConfirmModal();
      } catch (error) {
        console.log(error);
        showNotification('error', t('Error', { ns: 'common' }), t('Error when updating prices'));
        setConfirmModalLoading(false);
      }
    },
    [changeSizeCodesPrices, closeConfirmModal, form, onClose, selectedSizeCodes, setConfirmModalLoading, t],
  );

  const showRateChangeConfirmModal = useCallback((): void => {
    dispatch(
      globalConfirmModalActions.setGlobalConfirmModalData({
        title: t('Rates have been changed'),
        description: t(
          'You have changed one or more rates. Would you like to update the box prices accordingly? The rate for boxes in active contracts will remain as defined in the contract!',
        ),
        isConfirmDisabled: isForbidden,
        zIndex: 4000,
        onCancel: async () => {
          await submitChangePrices(false);
        },
        onOk: async () => {
          await submitChangePrices(true);
        },
      }),
    );
  }, [dispatch, isForbidden, submitChangePrices, t]);

  const handleSubmit = useCallback(
    (values: PriceChangeParams): void => {
      dispatch(changePricesModalActions.setPriceChangeParams(values));
      showRateChangeConfirmModal();
    },
    [dispatch, showRateChangeConfirmModal],
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onValuesChange={handleValuesChange}
      className="flex flex-col px-6 py-4"
      disabled={isForbidden}
    >
      <div className="font-semibold text-2xl mb-4">{t('Change prices')}</div>

      <div className="flex gap-10">
        <div className="flex-1">
          <div className="text-primaryLight mb-6">
            <div>
              {t('Warehouse')}: <span className="text-primary font-semibold">{selectedWarehouse?.name}</span>
            </div>
          </div>

          <Form.Item
            name="direction"
            label={t('Price change')}
            rules={[{ required: true, message: t('Please select increase or decrease') }]}
          >
            <RadioButtonGroup
              options={directionOptions}
              value={priceChangeParams.direction}
              onChange={changeDirection}
              isDisabled={isForbidden}
            />
          </Form.Item>

          <Form.Item
            name="percent"
            label={t('Percent')}
            className="w-[35%]"
            rules={[
              { required: true, message: t('Please enter percent') },
              { type: 'number', min: 0.01, message: t('Percent must be greater than 0') },
            ]}
          >
            <InputNumber bordered min={0.01} addonAfter="%" placeholder={t('Percent')} />
          </Form.Item>

          <Form.Item
            name="rateTypes"
            label={t('Rates')}
            rules={[
              { required: true, message: t('Please select at least one rate') },
              { type: 'array', min: 1, message: t('Please select at least one rate') },
            ]}
          >
            <CheckboxGroup name="rateTypes" options={rateTypeOptions} showSelectAll={false} className="flex flex-row gap-6" />
          </Form.Item>
        </div>

        <div className="flex-1 mb-3">
          <PricesPreview />
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" isLoading={isLoading} isDisabled={isForbidden}>
          {t('Save', { ns: 'common' })}
        </Button>
      </div>
    </Form>
  );
});
